import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://8h02jqezvc.execute-api.us-east-1.amazonaws.com/prod';

// Helper function to get authenticated headers
async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return {
      'Content-Type': 'application/json'
    };
  }
}

export interface WorkflowMetrics {
  executionArn: string;
  workflowType: string;
  status: string;
  duration?: number;
  startTime: string;
  endTime?: string;
  stepMetrics: StepMetric[];
  lambdaMetrics: LambdaMetric[];
  cloudWatchMetrics: CloudWatchMetric[];
  errorDetails?: ErrorDetail[];
}

export interface StepMetric {
  stepName: string;
  type: string;
  status: string;
  duration: number;
  startTime: string;
  endTime?: string;
  inputSize?: number;
  outputSize?: number;
  retryCount?: number;
}

export interface LambdaMetric {
  functionName: string;
  invocations: number;
  duration: number;
  errors: number;
  throttles: number;
  memoryUtilization: number;
  billedDuration: number;
  maxMemoryUsed: number;
  coldStarts: number;
}

export interface CloudWatchMetric {
  metricName: string;
  namespace: string;
  value: number;
  unit: string;
  timestamp: string;
  dimensions: Record<string, string>;
}

export interface ErrorDetail {
  stepName: string;
  errorType: string;
  errorMessage: string;
  timestamp: string;
  cause?: string;
}

export interface ObservabilityDashboard {
  overview: {
    totalExecutions: number;
    successRate: number;
    averageDuration: number;
    activeExecutions: number;
    errorRate: number;
  };
  trends: {
    executionTrends: TimeSeriesData[];
    durationTrends: TimeSeriesData[];
    errorTrends: TimeSeriesData[];
  };
  topErrors: ErrorSummary[];
  performanceMetrics: PerformanceMetric[];
  costMetrics: CostMetric[];
}

export interface TimeSeriesData {
  timestamp: string;
  value: number;
  label?: string;
}

export interface ErrorSummary {
  errorType: string;
  count: number;
  percentage: number;
  lastOccurrence: string;
  affectedWorkflows: string[];
}

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  change: number;
}

export interface CostMetric {
  service: string;
  cost: number;
  unit: string;
  period: string;
  breakdown: CostBreakdown[];
}

export interface CostBreakdown {
  component: string;
  cost: number;
  percentage: number;
}

export interface WorkflowInsight {
  type: 'info' | 'warning' | 'error' | 'success';
  category: 'performance' | 'reliability' | 'cost' | 'security';
  title: string;
  description: string;
  recommendation: string;
  priority: 'low' | 'medium' | 'high';
}

export interface WorkflowInsights {
  dashboard: ObservabilityDashboard;
  insights: WorkflowInsight[];
}

class ObservabilityService {
  async getWorkflowMetrics(executionArn: string): Promise<WorkflowMetrics> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/observability/workflow-metrics`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ executionArn })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.data;
  }

  async getObservabilityDashboard(
    workflowType?: string,
    timeRange: string = '24h'
  ): Promise<ObservabilityDashboard> {
    const params = new URLSearchParams();
    if (workflowType) params.append('workflowType', workflowType);
    params.append('timeRange', timeRange);

    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/observability/dashboard?${params.toString()}`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.data;
  }

  async getWorkflowInsights(
    workflowType?: string,
    timeRange: string = '7d'
  ): Promise<WorkflowInsights> {
    const params = new URLSearchParams();
    if (workflowType) params.append('workflowType', workflowType);
    params.append('timeRange', timeRange);

    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/observability/insights?${params.toString()}`, {
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.data;
  }

  // Utility methods for data formatting
  formatDuration(milliseconds: number): string {
    if (milliseconds < 1000) {
      return `${milliseconds}ms`;
    } else if (milliseconds < 60000) {
      return `${(milliseconds / 1000).toFixed(1)}s`;
    } else if (milliseconds < 3600000) {
      return `${(milliseconds / 60000).toFixed(1)}m`;
    } else {
      return `${(milliseconds / 3600000).toFixed(1)}h`;
    }
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  }

  formatPercentage(value: number): string {
    return `${(value * 100).toFixed(1)}%`;
  }

  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'succeeded':
      case 'success':
        return '#10B981'; // green
      case 'failed':
      case 'error':
        return '#EF4444'; // red
      case 'running':
      case 'in_progress':
        return '#3B82F6'; // blue
      case 'aborted':
      case 'cancelled':
        return '#F59E0B'; // yellow
      case 'timed_out':
        return '#F97316'; // orange
      default:
        return '#6B7280'; // gray
    }
  }

  getTrendIcon(trend: 'up' | 'down' | 'stable'): string {
    switch (trend) {
      case 'up':
        return '↗️';
      case 'down':
        return '↘️';
      case 'stable':
        return '→';
      default:
        return '→';
    }
  }

  getPriorityColor(priority: 'low' | 'medium' | 'high'): string {
    switch (priority) {
      case 'high':
        return '#EF4444'; // red
      case 'medium':
        return '#F59E0B'; // yellow
      case 'low':
        return '#10B981'; // green
      default:
        return '#6B7280'; // gray
    }
  }
}

export const observabilityService = new ObservabilityService();
